import { PATIENT_STATUS } from '@constants/PatientManagement/status'
import type { IContact, IContactGroup } from '@dataTypes/contact.type'
import type { IChatConversation } from '@dataTypes/chat.type'
import { StatusTypes } from '@components/common/StatusBadge/StatusBadge.component'
import { formatPhoneNumber } from '@utils/numberFormatter.util'
import { getFullName } from '@utils/fullname.util'

export interface AddChatRecipient {
  name: string
  type: 'contact' | 'group'
  id: string
  public_id: string
  status?: StatusTypes
  phoneDisplay: string
  secondaryLabel?: string
  isUnknown?: boolean
  sms: boolean
  messenger: boolean
  instagram: boolean
  photo?: string
  contact?: IContact
  group?: IContactGroup
}

export function buildRecipientFromContact(contact: IContact): AddChatRecipient {
  const phone = contact.cellphone?.trim() ?? ''
  return {
    name: getFullName(contact),
    type: 'contact',
    id: contact.contact_id!,
    public_id:
      contact.patient?.public_id ?? contact.provider?.public_id ?? 'N/A',
    status:
      contact.patient?.patient_status !== undefined
        ? PATIENT_STATUS[contact.patient.patient_status]
        : ((contact.provider?.work_status as StatusTypes) ?? undefined),
    phoneDisplay: phone ? formatPhoneNumber(phone) : '—',
    sms: !!contact.cellphone,
    messenger: !!contact.patient?.meta_id,
    instagram: !!contact.patient?.meta_id,
    photo: contact.patient?.photo ?? contact.photo,
    contact,
  }
}

export function buildUnknownPhoneRecipient(
  phoneNumber: string,
): AddChatRecipient {
  const formatted = formatPhoneNumber(phoneNumber)
  return {
    name: formatted,
    type: 'contact',
    id: `unknown-${phoneNumber}`,
    public_id: '',
    phoneDisplay: 'Unknown',
    secondaryLabel: 'Unknown',
    isUnknown: true,
    sms: true,
    messenger: false,
    instagram: false,
  }
}

function getConversationLastActivity(convo: IChatConversation): number {
  const messageTimes = (convo.chat_message ?? []).map((m) =>
    new Date(m.timestamp).getTime(),
  )
  const latestMessage = messageTimes.length ? Math.max(...messageTimes) : 0
  const updatedAt = convo.date_time_updated_at
    ? new Date(convo.date_time_updated_at).getTime()
    : 0
  return Math.max(latestMessage, updatedAt)
}

export function buildRecentlyContactedRecipients(
  conversations: IChatConversation[] | null | undefined,
  contacts: IContact[],
  limit = 5,
): AddChatRecipient[] {
  if (!conversations?.length) return []

  const sorted = [...conversations]
    .filter(
      (convo) => !convo.is_archived && convo.chat_participant?.length === 1,
    )
    .sort(
      (a, b) => getConversationLastActivity(b) - getConversationLastActivity(a),
    )

  const seen = new Set<string>()
  const results: AddChatRecipient[] = []

  for (const convo of sorted) {
    const participant = convo.chat_participant[0]
    const contactId = participant.contact_id
    const dedupeKey = contactId ?? participant.phone_number ?? ''
    if (!dedupeKey || seen.has(dedupeKey)) continue
    seen.add(dedupeKey)

    if (contactId) {
      const contact = contacts.find((c) => c.contact_id === contactId)
      if (contact) {
        results.push(buildRecipientFromContact(contact))
      } else if (participant.phone_number) {
        results.push(buildUnknownPhoneRecipient(participant.phone_number))
      }
    } else if (participant.phone_number) {
      results.push(buildUnknownPhoneRecipient(participant.phone_number))
    }

    if (results.length >= limit) break
  }

  return results
}
